import { AvatarDropdown, AvatarName, Footer, Question, SelectLang } from '@/components';
import { HubHeaderSegment, isSkillHubPath, isOpensourcePath } from '@/components/HubMenuHeader';
// import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import type { MenuDataItem } from '@ant-design/pro-layout';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, useIntl, useLocation } from '@umijs/max';
import { Button } from 'antd';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { GetAKBtn, GithubBtn } from './components/RightContent';

const isDev = process.env.NODE_ENV === 'development';
// const loginPath = '/user/login';

/** 关闭侧栏菜单项 Tooltip，避免 antd Tooltip + ResizeObserver 在 React 18 下触发 findDOMNode 弃用警告（见 ProLayout BaseMenu MenuItemTooltip） */
function menuDisableItemTooltip(items: MenuDataItem[]): MenuDataItem[] {
  return (items || []).map((item) => ({
    ...item,
    disabledTooltip: true,
    children: item.children?.length ? menuDisableItemTooltip(item.children) : item.children,
  }));
}

/** 顶部右侧 GitHub / 语言 / 获取 AK：获取 AK 在所有区域都展示，其他仅在 MCP Server 展示 */
const McpServerHeaderActions: React.FC = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const isSkillHub = isSkillHubPath(pathname);
  const isOpensource = isOpensourcePath(pathname);
  const showOpensourceAdd =
    isOpensource && pathname !== '/opensource/add';

  if (isSkillHub || isOpensource) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {showOpensourceAdd && (
          <Button
            type="primary"
            style={{
              width: 120,
              height: 40,
              borderRadius: 12,
              fontSize: 14,
              paddingInline: 0,
            }}
            onClick={() => history.push('/opensource/add')}
          >
            {intl.formatMessage({ id: 'pages.opensource.addProject' })}
          </Button>
        )}
         <SelectLang key="SelectLang" />
        <GetAKBtn key="LoginBtn" />
      </div>
    );
  }

  return (
    <>
      <GithubBtn key="GithubBtn" />
      <SelectLang key="SelectLang" />
      <GetAKBtn key="LoginBtn" />
    </>
  );
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  // const fetchUserInfo = async () => {
  //   try {
  //     const msg = await queryCurrentUser({
  //       skipErrorHandler: true,
  //     });
  //     return msg.data;
  //   } catch (error) {
  //     // history.push(loginPath);
  //   }
  //   return undefined;
  // };
  // 如果不是登录页面，执行
  // const { location } = history;
  // if (location.pathname !== loginPath) {
  //   const currentUser = await fetchUserInfo();
  //   return {
  //     fetchUserInfo,
  //     currentUser,
  //     settings: defaultSettings as Partial<LayoutSettings>,
  //   };
  // }
  return {
    // fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    logo: <img src="/images/logo.png" alt="logo" style={{ height: 32, marginRight: 8 }} />,
    siderWidth:200,
    collapsedButtonRender:false,
    style:{
      maxWidth: '100%', // 设置最大宽度
      margin: '0 auto', // 居中显示
    },
    breakpoint: 'md',
    token: {
      bgLayout: '#f8f8f7', // 设置 ProLayout 的背景色
      colorBgContainer: '#ffffff', // 设置容器背景色
      layout: {
        header: {
          heightLayoutHeader: 207,
        },
      },
    },
    /** 顶部栏中部（Logo 与右侧 GitHub/语言/获取AK 之间） */
    headerContentRender: () => <HubHeaderSegment />,
    menuDataRender: (menuData) => {
      const { pathname } = history.location;
      const skillHub = isSkillHubPath(pathname);
      const opensource = isOpensourcePath(pathname);
      const mcpPaths = [
        '/serviceOverview',
        '/preparationForUse',
        '/quickStart',
        '/updateLog',
        '/contactUs',
      ];
      const filtered = (menuData || []).filter((item) => {
        const p = item.path || '';
        // Skill Hub / Open Source 区域不展示侧栏「技能广场 / 构建技能 / 开源」
        if (p.startsWith('/skill')) return false;
        if (p.startsWith('/opensource')) return false;
        if (mcpPaths.includes(p)) return false;
        return !(skillHub || opensource);
      });
      return menuDisableItemTooltip(filtered);
    },
    /** Skill Hub / Open Source 下侧栏菜单已为空，隐藏侧栏避免空白占位 */
    menuRender: (_props, defaultDom) => {
      const p = history.location.pathname;
      const mcpPaths = [
        '/serviceOverview',
        '/preparationForUse',
        '/quickStart',
        '/updateLog',
        '/contactUs',
      ];
      if (isSkillHubPath(p) || isOpensourcePath(p)) return false;
      if (mcpPaths.includes(p)) return false;
      return defaultDom;
    },
    actionsRender: () => [<McpServerHeaderActions key="McpServerHeaderActions" />],
    // avatarProps: {
    //   src: initialState?.currentUser?.avatar,
    //   title: <AvatarName />,
    //   render: (_, avatarChildren) => {
    //     return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
    //   },
    // },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      // const { location } = history;
      // // 如果没有登录，重定向到 login
      // if (!initialState?.currentUser && location.pathname !== loginPath) {
      //   history.push(loginPath);
      // }
    },
    // bgLayoutImgList: [
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
    //     left: 85,
    //     bottom: 100,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
    //     bottom: -68,
    //     right: -45,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
    //     bottom: 0,
    //     left: 0,
    //     width: '331px',
    //   },
    // ],
    // links: isDev
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPI 文档</span>
    //       </Link>,
    //     ]
    //   : [],
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {/* ProLayout 头部样式：mix 双 header 说明 + 83px 顶栏固定（随页面滚动仍贴顶） */}
          <style>{`
            /*
             * 为何有两个 header？
             * layout 为 mix 时 needFixedHeader 为 true（见 pro-layout Header/index.js），会连续渲染两个 antd Layout.Header：
             * 1）仅作高度占位，无 .ant-pro-layout-header，给整块 fixed 顶栏预留文档流空间；
             * 2）真正的 .ant-pro-layout-header，内含 GlobalHeader。
             * 我们改为只把 83px 导航条 fixed，整块顶栏不再 fixed，占位条会重复留白，故隐藏第 1 个。
             * body 为整页滚动（global.less），原先 relative 顶栏会随滚动离开视口；fixed 后 Logo/Tab/按钮始终贴顶。
             */
            .ant-pro-layout header.ant-layout-header:not(.ant-pro-layout-header) {
              display: none !important;
              height: 0 !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              border: none !important;
              line-height: 0 !important;
              overflow: hidden !important;
            }
            /* 总高 207px：下方约 124px 为头图区，随页面滚动可移出视口；83px 导航单独 fixed */
            .ant-pro-layout .ant-layout-header.ant-pro-layout-header-fixed-header,
            .ant-pro-layout .ant-layout-header.ant-pro-layout-header {
              position: relative !important;
              height: 207px !important;
              min-height: 207px !important;
              box-sizing: border-box !important;
              padding: 0 !important;
              line-height: normal !important;
              display: flex !important;
              flex-direction: column !important;
              align-items: stretch !important;
              justify-content: flex-start !important;
              background: url('/images/headerBg.png') no-repeat center center !important;
              background-size: cover !important;
              background-color: #0a0f2a !important;
              backdrop-filter: none !important;
              box-shadow: none !important;
              border-bottom: none !important;
            }
            .ant-pro-layout .ant-pro-global-header {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              z-index: 105 !important;
              display: flex !important;
              flex: 0 0 83px !important;
              width: 100% !important;
              height: 83px !important;
              min-height: 83px !important;
              max-height: 83px !important;
              margin-inline: 0 !important;
              box-sizing: border-box !important;
              align-items: center !important;
              padding: 0 50px 0 56px !important;
              background: rgba(10, 19, 68, 0.69) !important;
            }
            .ant-pro-layout .ant-pro-global-header .ant-pro-global-header-header-actions {
              color: rgba(255, 255, 255, 0.92) !important;
            }
            .ant-pro-layout .ant-pro-global-header .ant-pro-global-header-header-actions .anticon {
              color: rgba(255, 255, 255, 0.92) !important;
            }
            .ant-pro-layout .ant-pro-layout-header .ant-pro-header-logo a,
            .ant-pro-layout .ant-pro-global-header .ant-pro-global-header-logo a {
              display: flex !important;
              align-items: center !important;
            }
          `}</style>
          {/* {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )} */}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
